import { Box, Button, Fade, Grid, InputAdornment, Theme, Typography } from '@mui/material';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';
import * as yup from 'yup';
import ButtonWithFeddback, { ButtonStatusVariant } from '../../components/ButtonWithFeddback';
import useWidth from '../../components/Hooks/useWidth';
import RFFAvatarUpload from '../../components/RFFAvatarUpload';
import RFFSelect from '../../components/ReactFinalForms/RFFSelect';
import RFFTextField from '../../components/ReactFinalForms/RFFTextField';
import RFFToggleButtonList from '../../components/ReactFinalForms/RFFToggleButtonList';
import BannerForm from '../../components/Site/BannerForm';
import useImageUpload from '../../hooks/useImageUpload';
import useMoney from '../../hooks/useMoney';
import LangEnum from '../../lang/LangEnum';
import { socialMediasConfig } from '../../lib/SocialMedias';
import yupValidation from '../../lib/yupValidation';
import { brandShowroomRepository } from '../../services/modules/brand-showroom/brand-showroom.repository';
import {
  BrandShowroom,
  BrandShowroomAbout,
} from '../../services/modules/brand-showroom/brand-showroom.types';
import snackbarStore from '../../stores/Snackbar.store';
import { WholesaleMediumPricesTypeEnum } from '../../types/Enums';
import teceoInputConfig from '../../utils/TeceoInputConfig';
import teceoSelectConfig from '../../utils/TeceoSelectConfig';
import { isAdminTeceo } from '../context/ApplicationContext/applicationContext.utils';
import useSegments, { SegmentsTranslation, translateSegments } from '../segments/useSegments';
import { useTranslation } from '../translation/TranslationContext';

const useStyles = makeStyles()((theme: Theme) => ({
  avatar: {
    width: theme.spacing(19),
    height: theme.spacing(19),
  },
  selectInputItem: {
    textTransform: 'none',
  },
}));

type BrandAboutSectionFormProps = {
  initialData: Partial<BrandShowroom> | undefined;
  onSave: (values: BrandShowroomAbout) => void;
  onCancel: () => void;
};
const repository = brandShowroomRepository();
const BrandAboutSectionForm = ({ initialData, onSave, onCancel }: BrandAboutSectionFormProps) => {
  const { classes } = useStyles();
  const { mobileWidth } = useWidth();
  const { handleMessage } = snackbarStore;
  const [formStatus, setFormStatus] = useState<ButtonStatusVariant>('normal');
  const [segmentsState] = useSegments({ autoLoad: true });
  const [bannerIsLoading, setBannerIsLoading] = useState<boolean>(false);
  const [bannerUrl, setBannerUrl] = useState<string | null>(initialData?.bannerUrl || null);
  const { formatMoney } = useMoney();

  const imageUpload = useImageUpload();

  const { translate, isPortuguese } = useTranslation();

  const validationSchema = yup.object().shape({
    commercialName: yup.string().trim().required(translate(LangEnum.GLOBAL_REQUIRED)),
    title: yup
      .string()
      .trim()
      .nullable()
      .max(100, translate(LangEnum.GLOBAL_CHARACTERS_MAX_LIMIT, { num: 100 })),
    website: yup
      .string()
      .trim()
      .nullable()
      .max(100, translate(LangEnum.GLOBAL_CHARACTERS_MAX_LIMIT, { num: 100 })),
    aboutMe: yup
      .string()
      .trim()
      .nullable()
      .max(450, translate(LangEnum.GLOBAL_CHARACTERS_MAX_LIMIT, { num: 450 })),
  });

  const validate = async (values: any) => yupValidation(validationSchema)({ ...values });

  const handleChangeBanner = async (file?: File) => {
    setBannerIsLoading(true);
    const newBannerUrl = file ? await imageUpload.uploadImage(file, 1920) : null;
    setBannerUrl(newBannerUrl);
    setBannerIsLoading(false);
  };

  const onSubmit = async (data: BrandShowroomAbout) => {
    setFormStatus('loading');

    const dataToSave = { ...data };
    if (!isAdminTeceo()) {
      delete dataToSave.commercialName;
    }

    if (dataToSave.instagramUrl) {
      dataToSave.instagramUrl = dataToSave.instagramUrl.replace(/^@/, '');
    }

    await repository.saveBanner({ bannerUrl });
    const response = await repository.saveAbout(dataToSave);
    if (response.ok) {
      onSave(data);
      setFormStatus('success');
    } else {
      handleMessage({
        type: 'error',
        message: translate(LangEnum.SHOWROOM_ERROR),
      });
      setFormStatus('error');
    }
    return response;
  };

  const initialValues = {
    ...initialData,
    segmentIds: initialData?.segments?.map((segment) => segment.id),
  };

  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      <Grid container spacing={mobileWidth ? 2 : 6}>
        <Grid size={12}>
          <Typography color="textSecondary">
            {translate(LangEnum.SHOWROOM_ADD_BANNER)}
            <Box
              component="span"
              sx={{
                marginLeft: 1,
                fontStyle: 'italic',
              }}
            >
              {translate(LangEnum.STOREFRONT_ADD_STORE_PHOTO_WIDTHS)}
            </Box>
          </Typography>
          <BannerForm
            onChange={handleChangeBanner}
            loading={bannerIsLoading}
            bannerUrl={bannerUrl}
            type="showroom"
          />
        </Grid>
        <Grid size={12}>
          <Form
            keepDirtyOnReinitialize
            onSubmit={onSubmit}
            initialValues={initialValues}
            validate={validate}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={6}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 8,
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <Grid
                        size={{
                          xs: 12,
                          sm: 'auto',
                        }}
                      >
                        <Typography align="center" color="textSecondary">
                          {translate(LangEnum.SHOWROOM_ADD_LOGO, { br: <br /> })}
                        </Typography>
                        <RFFAvatarUpload
                          avatarProps={{ className: classes.avatar }}
                          name="logoUrl"
                        />
                      </Grid>

                      <Grid
                        size={{
                          xs: 12,
                          sm: 'grow',
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <RFFTextField
                              {...teceoInputConfig}
                              autoFocus
                              name="commercialName"
                              label={translate(LangEnum.SHOWROOM_BRAND_NAME)}
                              disabled={!isAdminTeceo()}
                            />
                          </Grid>
                          <Grid size={12}>
                            <RFFTextField {...teceoInputConfig} name="title" label="slogan" />
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              sm: 6,
                            }}
                          >
                            <RFFTextField {...teceoInputConfig} name="website" label="site" />
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              sm: 6,
                            }}
                          >
                            <RFFSelect
                              items={WholesaleMediumPricesTypeEnum.values.map((item: any) => ({
                                ...item,
                                label: translate(item.label, {
                                  price: formatMoney(item.lowerValue),
                                  priceFrom: formatMoney(item.lowerValue),
                                  priceTo: formatMoney(item.biggerValue),
                                }),
                              }))}
                              menuItemProps={{ className: classes.selectInputItem }}
                              name="wholesaleMediumPrice"
                              label={translate(LangEnum.SHOWROOM_AVERAGE_WHOLESALE_PRICE)}
                              {...teceoSelectConfig}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid size={12}>
                        <RFFTextField
                          {...teceoInputConfig}
                          name="aboutMe"
                          label={translate(LangEnum.SHOWROOM_ABOUT)}
                          multiline
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          placeholder={translate(LangEnum.SHOWROOM_ABOUT_ME_PLACEHOLDER)}
                          rows={4}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 4,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <RFFTextField
                          {...teceoInputConfig}
                          name="instagramUrl"
                          label="instagram"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <socialMediasConfig.INSTAGRAM.icon color="disabled" />
                                  </InputAdornment>
                                  <InputAdornment position="start">instagram.com/</InputAdornment>
                                </>
                              ),
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={12}>
                        <RFFTextField
                          {...teceoInputConfig}
                          name="facebookUrl"
                          label="facebook"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <socialMediasConfig.FACEBOOK.icon color="disabled" />
                                  </InputAdornment>
                                  <InputAdornment position="start">facebook.com/</InputAdornment>
                                </>
                              ),
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={12}>
                        <RFFTextField
                          {...teceoInputConfig}
                          name="twitterUrl"
                          label="twitter"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <socialMediasConfig.TWITTER.icon color="disabled" />
                                  </InputAdornment>
                                  <InputAdornment position="start">twitter.com/</InputAdornment>
                                </>
                              ),
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={12}>
                        <RFFTextField
                          {...teceoInputConfig}
                          name="pinterestUrl"
                          label="pinterest"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <socialMediasConfig.PINTEREST.icon color="disabled" />
                                  </InputAdornment>
                                  <InputAdornment position="start">pinterest.com/</InputAdornment>
                                </>
                              ),
                            },
                          }}
                        />
                      </Grid>

                      <Grid size={12}>
                        <RFFTextField
                          {...teceoInputConfig}
                          name="youtubeUrl"
                          label="youtube"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <socialMediasConfig.YOUTUBE.icon color="disabled" />
                                  </InputAdornment>
                                  <InputAdornment position="start">youtube.com/</InputAdornment>
                                </>
                              ),
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Fade in={segmentsState.items.length > 0}>
                    <Grid size={12}>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Typography variant="h4" gutterBottom>
                            {translate(LangEnum.STOREFRONT_TYPES_OF_PRODUCTS)}
                          </Typography>
                          <Typography color="textSecondary">
                            {translate(LangEnum.SHOWROOM_TYPES_OF_PRODUCTS_TEXT)}
                          </Typography>
                        </Grid>
                        <Grid size={12}>
                          <RFFToggleButtonList
                            items={
                              segmentsState.options.map((segment) => ({
                                ...segment,
                                label: translateSegments(
                                  segment.label as keyof SegmentsTranslation,
                                  isPortuguese,
                                ),
                              })) || []
                            }
                            name="segmentIds"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Fade>

                  <Grid size={12}>
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Grid size="auto">
                        <Button onClick={onCancel}>
                          {translate(LangEnum.GLOBAL_CANCEL_LABEL)}
                        </Button>
                      </Grid>
                      <Grid size="auto">
                        <ButtonWithFeddback
                          variant="contained"
                          color="primary"
                          type="submit"
                          status={formStatus}
                        >
                          {translate(LangEnum.GLOBAL_SAVE)}
                        </ButtonWithFeddback>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BrandAboutSectionForm;
